/**
 * MAMAVIBEZ SHOPIFY THEME - PRODUCT GALLERY & CIRCULAR MAGNIFIER LOUPE ENGINE
 */

class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.main = this.querySelector('[data-gallery-main]');
    this.thumbs = this.querySelectorAll('[data-gallery-thumbs] button');
    this.items = this.querySelectorAll('.product-gallery__item');
    this.currentIndex = 0;
    this.zoomFactor = 2.5;
    this.loupeSize = 180;
    this.initEvents();
    this.initMagnifier();
  }

  initEvents() {
    // Thumbnail clicks -> show big preview
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        const index = parseInt(thumb.dataset.index, 10);
        this.setActive(index);
      });
    });

    // Mobile prev/next navigation
    const prevBtn = this.querySelector('.gallery-nav-btn--prev');
    const nextBtn = this.querySelector('.gallery-nav-btn--next');

    prevBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      const nextIndex = (this.currentIndex - 1 + this.items.length) % this.items.length;
      this.setActive(nextIndex);
    });

    nextBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      const nextIndex = (this.currentIndex + 1) % this.items.length;
      this.setActive(nextIndex);
    });

    // Variant change listener (switch image to match variant if variant has media)
    document.addEventListener('variant:change', (e) => {
      const variant = e.detail?.variant;
      if (variant && variant.featured_media) {
        const matchingThumb = Array.from(this.thumbs).find(
          (t) => t.dataset.mediaId == variant.featured_media.id
        );
        if (matchingThumb) {
          const index = parseInt(matchingThumb.dataset.index, 10);
          this.setActive(index);
        }
      }
    });

    // Mobile touch swipe gestures
    let touchStartX = 0;
    let touchEndX = 0;

    this.main?.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.main?.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      if (touchEndX < touchStartX - 45) {
        // swipe left -> next
        const nextIndex = (this.currentIndex + 1) % this.items.length;
        this.setActive(nextIndex);
      }
      if (touchEndX > touchStartX + 45) {
        // swipe right -> prev
        const nextIndex = (this.currentIndex - 1 + this.items.length) % this.items.length;
        this.setActive(nextIndex);
      }
    }, { passive: true });
  }

  setActive(index) {
    if (index === this.currentIndex || !this.items[index]) return;
    this.currentIndex = index;

    // Switch big preview active item
    this.items.forEach((item, i) => {
      item.classList.toggle('is-active', i === index);
    });

    // Switch thumbnail active state and scroll into view
    this.thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
      if (i === index) {
        thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    });

    // Update counter on mobile
    const counter = this.querySelector('[data-current-index]');
    if (counter) counter.textContent = index + 1;

    // Preload zoomed image for the new active item
    this.preloadActiveZoomImage();
  }

  preloadActiveZoomImage() {
    const activeItem = this.items[this.currentIndex];
    const img = activeItem?.querySelector('.product-gallery__image');
    const zoomSrc = img?.dataset.zoomSrc || img?.currentSrc || img?.src;
    if (zoomSrc) {
      const preload = new Image();
      preload.src = zoomSrc;
    }
  }

  initMagnifier() {
    // Only initialize desktop hover magnifying circle
    if (window.matchMedia('(hover: none)').matches) return;

    this.items.forEach((item) => {
      const container = item.querySelector('[data-zoom-container]');
      const img = container?.querySelector('.product-gallery__image');
      const loupe = container?.querySelector('.product-gallery__loupe');

      if (!container || !img || !loupe) return;

      const half = this.loupeSize / 2;

      container.addEventListener('mouseenter', () => {
        const zoomSrc = img.dataset.zoomSrc || img.currentSrc || img.src;
        if (!zoomSrc) return;

        loupe.style.backgroundImage = `url("${zoomSrc}")`;
        loupe.classList.add('is-visible');
      });

      container.addEventListener('mouseleave', () => {
        loupe.classList.remove('is-visible');
      });

      container.addEventListener('mousemove', (e) => {
        const rect = container.getBoundingClientRect();
        let x = e.clientX - rect.left;
        let y = e.clientY - rect.top;

        // Clamp inside bounds
        x = Math.max(0, Math.min(x, rect.width));
        y = Math.max(0, Math.min(y, rect.height));

        // Position the circle loupe centered on the mouse
        loupe.style.left = `${x - half}px`;
        loupe.style.top = `${y - half}px`;

        // Calculate magnified background image sizing and offset
        const bgW = rect.width * this.zoomFactor;
        const bgH = rect.height * this.zoomFactor;
        const bgX = (x * this.zoomFactor) - half;
        const bgY = (y * this.zoomFactor) - half;

        loupe.style.backgroundSize = `${bgW}px ${bgH}px`;
        loupe.style.backgroundPosition = `-${bgX}px -${bgY}px`;
      });
    });
  }
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}
